import React, { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import InterviewRescheduleSlotEditor, {
  createRescheduleSlotDraft,
  formatRescheduleSlotSummary,
} from '../../HireWithUs/Pages/Applications/InterviewRescheduleSlotEditor';
import { resolveHostSlotDurationMinutes, timeValueToMinutes } from '../../HireWithUs/Pages/Applications/meetingScheduleUtils';
import { buildAvailabilityNotesFromSlots } from '../utils/bookingActionUtils';

export default function BookingMultiSlotPicker({
  timezone,
  actor = 'candidate',
  disabled = false,
  slotDurationMinutes,
  onNotesChange,
  onSlotsChange,
  resetKey = 0,
}) {
  const resolvedSlotDuration = useMemo(
    () => resolveHostSlotDurationMinutes(slotDurationMinutes),
    [slotDurationMinutes]
  );
  const [draft, setDraft] = useState(() => createRescheduleSlotDraft());
  const [slots, setSlots] = useState([]);

  const previewNotes = useMemo(
    () => buildAvailabilityNotesFromSlots(slots, timezone, resolvedSlotDuration),
    [slots, timezone, resolvedSlotDuration]
  );

  React.useEffect(() => {
    onNotesChange?.(previewNotes);
  }, [previewNotes, onNotesChange]);

  React.useEffect(() => {
    onSlotsChange?.(slots);
  }, [onSlotsChange, slots]);

  React.useEffect(() => {
    setDraft(createRescheduleSlotDraft());
    setSlots([]);
  }, [resetKey]);

  const addDraftSlot = () => {
    if (!draft?.date || !draft?.startTime || !draft?.endTime) return;
    const startMinutes = timeValueToMinutes(draft.startTime);
    const endMinutes = timeValueToMinutes(draft.endTime);
    if (
      startMinutes != null &&
      endMinutes != null &&
      endMinutes <= startMinutes
    ) {
      return;
    }
    const next = {
      ...draft,
      id: `slot-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    };
    setSlots((current) => [...current, next]);
    setDraft(createRescheduleSlotDraft({ date: draft.date }));
  };

  const removeSlot = (slotId) => {
    setSlots((current) => current.filter((slot) => slot.id !== slotId));
  };

  const heading =
    actor === 'interviewer'
      ? 'Propose a custom interview time'
      : 'Share when you are available';
  const intro =
    actor === 'interviewer'
      ? 'Choose one or more interview times to send to the candidate.'
      : 'If none of the listed times work, add two or more times when you can attend.';

  return (
    <div className="li-booking-availability">
      <div className="li-booking-section-head">
        <h2 className="li-booking-section-title">{heading}</h2>
        <p className="li-booking-section-copy">{intro}</p>
      </div>

      <InterviewRescheduleSlotEditor
        timezone={timezone}
        draft={draft}
        onDraftChange={setDraft}
        disabled={disabled}
      />

      <div className="li-booking-availability-actions">
        <button
          type="button"
          className="li-booking-btn li-booking-btn-secondary"
          disabled={disabled || !draft?.date || !draft?.startTime || !draft?.endTime}
          onClick={addDraftSlot}
        >
          <Plus size={16} />
          Add this time
        </button>
      </div>

      {slots.length ? (
        <div className="li-booking-selected-slots">
          <div className="li-booking-selected-slots-head">
            Selected times ({slots.length})
          </div>
          <div className="li-booking-selected-slots-list">
            {slots.map((slot) => (
              <div key={slot.id} className="li-booking-selected-slot-row">
                <span>{formatRescheduleSlotSummary(slot, timezone)}</span>
                <button
                  type="button"
                  className="li-booking-icon-btn"
                  disabled={disabled}
                  onClick={() => removeSlot(slot.id)}
                  aria-label="Remove time"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
